import { Box, Button, LinearProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import AddIcon from '@mui/icons-material/Add'
import { coursePromotionApi } from '../../../../../../features/course/coursePromotion'
import { CoursePromotionState } from '../../../../../../features/types'
import PromoDialog from '../components/PromoDialog'
import PromoTable from '../components/PromoTable'
import Sentinel from '../../../../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../../../../app/hooks/usePaginatedList'

interface Props {
    coursePriceId: number
}

const styles = {
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
}

const PromoTab: React.FC<Props> = ({ coursePriceId }) => {
    const { formatMessage } = useIntl()
    const [openPromoDialog, setOpenPromoDialog] = useState<boolean>(false)
    const [currentRowData, setCurrentRowData] =
        useState<CoursePromotionState>(null)
    const [fetchPromotions] =
        coursePromotionApi.useLazyGetPromotionsByCoursePriceQuery()

    const {
        rows,
        isLoading: isFetching,
        loadedPages,
        sentinelRef,
        reload,
    } = usePaginatedList(
        (page, size) =>
            fetchPromotions({ coursePriceId, page, size }).unwrap(),
        [coursePriceId],
        { skip: !coursePriceId },
    )

    const [deletePromotion, { isLoading: isDeleting }] =
        coursePromotionApi.useDeleteCoursePromotionMutation()
    const isLoading = (isFetching && loadedPages === 0) || isDeleting

    const handleRowUpdate = (row: CoursePromotionState) => {
        setCurrentRowData(row)
        setOpenPromoDialog(true)
    }

    const handleDelete = async (id: number) => {
        try {
            await deletePromotion(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            reload()
        } catch (e) {}
    }

    useEffect(() => {
        if (!openPromoDialog) {
            setCurrentRowData(null)
        }
    }, [openPromoDialog])

    return (
        <Box sx={styles.flexColumn}>
            <Box
                ml="auto"
                mb={2}
            >
                <Button
                    onClick={() => setOpenPromoDialog(true)}
                    variant="contained"
                    endIcon={<AddIcon />}
                >
                    {formatMessage({ id: 'addPromo' })}
                </Button>
            </Box>
            
            {isLoading ? (
                <LinearProgress />
            ) : (
                <>
                    <PromoTable
                        promotions={rows}
                        isFetching={isFetching}
                        onDelete={handleDelete}
                        onEdit={handleRowUpdate}
                    />

                    <Sentinel ref={sentinelRef} isLoading={isFetching} />
                </>
            )}

            <PromoDialog
                coursePriceId={coursePriceId}
                open={openPromoDialog}
                editPromo={currentRowData}
                handleClose={() => setOpenPromoDialog(false)}
            />
        </Box>
    )
}

export default PromoTab
